"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { findAncestorIds } from "./tree"
import type { LinkRenderer, NavSection } from "./types"

const COLLAPSED_STORAGE_KEY = "v2-sidebar-collapsed"
const WIDTH_STORAGE_KEY = "v2-sidebar-width"

/** Default, and the range the resize handle is clamped to. */
export const SIDEBAR_DEFAULT_WIDTH = 252
export const SIDEBAR_MIN_WIDTH = 208
export const SIDEBAR_MAX_WIDTH = 400

/**
 * Dragging narrower than this folds the sidebar to the rail instead of pinning
 * at the minimum — past a point the panel is too narrow to read, and the rail is
 * what you actually wanted.
 */
const FOLD_BELOW = 180

interface SidebarContextValue {
  collapsed: boolean
  toggleCollapsed: () => void
  isExpanded: (id: string) => boolean
  toggleExpanded: (id: string) => void
  width: number
  setWidth: (width: number) => void
  resetWidth: () => void
  resizing: boolean
  setResizing: (resizing: boolean) => void
  currentPath: string
  renderLink: LinkRenderer
}

const SidebarContext = createContext<SidebarContextValue | null>(null)

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext)
  if (!context) throw new Error("useSidebar must be used inside a <SidebarProvider>")
  return context
}

export interface SidebarProviderProps {
  sections: NavSection[]
  currentPath: string
  renderLink: LinkRenderer
  children: ReactNode
}

export function SidebarProvider({
  sections,
  currentPath,
  renderLink,
  children,
}: SidebarProviderProps) {
  const [collapsed, setCollapsed] = useState(
    () => globalThis.localStorage?.getItem(COLLAPSED_STORAGE_KEY) === "true",
  )
  const [resizing, setResizing] = useState(false)
  const [width, setWidthState] = useState(() => {
    const stored = Number(globalThis.localStorage?.getItem(WIDTH_STORAGE_KEY))
    return Number.isFinite(stored) && stored > 0 ? stored : SIDEBAR_DEFAULT_WIDTH
  })

  const setWidth = useCallback((next: number) => {
    // The pill folds and unfolds as well as resizing: drag past the threshold in
    // either direction and the rail toggles, so it is one continuous gesture
    // rather than a drag that dead-ends at the minimum.
    const shouldCollapse = next < FOLD_BELOW
    setCollapsed(shouldCollapse)
    if (shouldCollapse) return
    setWidthState(Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(next))))
  }, [])

  // Persisted once the value settles rather than inside `setWidth`, which a drag
  // calls on every pointer move — that was a synchronous localStorage write per
  // frame, on the main thread, during the one interaction that has to stay
  // smooth. Toggling the rail persists immediately, since no drag is in flight.
  useEffect(() => {
    if (resizing) return
    globalThis.localStorage?.setItem(COLLAPSED_STORAGE_KEY, String(collapsed))
    globalThis.localStorage?.setItem(WIDTH_STORAGE_KEY, String(width))
  }, [collapsed, width, resizing])

  const resetWidth = useCallback(() => setWidth(SIDEBAR_DEFAULT_WIDTH), [setWidth])

  const ancestorIds = useMemo(
    () => findAncestorIds(sections, currentPath),
    [sections, currentPath],
  )

  // Expansion is real state seeded from the active route, not a derivation of
  // it. Navigation only ever *adds* the new route's ancestors, so a node the
  // user expanded or collapsed by hand keeps that state instead of being
  // inverted the next time the route changes.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(ancestorIds))

  useEffect(() => {
    setExpandedIds(current => {
      const missing = ancestorIds.filter(id => !current.has(id))
      if (missing.length === 0) return current
      const next = new Set(current)
      missing.forEach(id => next.add(id))
      return next
    })
  }, [ancestorIds])

  const toggleCollapsed = useCallback(() => setCollapsed(current => !current), [])

  const isExpanded = useCallback((id: string) => expandedIds.has(id), [expandedIds])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(current => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const value = useMemo(
    () => ({
      collapsed,
      toggleCollapsed,
      isExpanded,
      toggleExpanded,
      currentPath,
      renderLink,
      width,
      setWidth,
      resetWidth,
      resizing,
      setResizing,
    }),
    [
      collapsed,
      toggleCollapsed,
      isExpanded,
      toggleExpanded,
      currentPath,
      renderLink,
      width,
      setWidth,
      resetWidth,
      resizing,
    ],
  )

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}
